import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import UpcomingFixtures from './components/UpcomingFixtures';
import RecentResults from './components/RecentResults';
import DashboardSection from './components/DashboardSection';
import RegistrationTrendChart from './components/RegistrationTrendChart';
import MatchCompletionTrendChart from './components/MatchCompletionTrendChart';
import StatusPieChart from './components/StatusPieChart';
import RegionalDistributionChart from './components/RegionalDistributionChart';
import PendingApprovalsPanel from './components/PendingApprovalsPanel';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../contexts/AuthContext';

const KPI_CONFIG = [
  { key: 'teams', title: 'Club Teams', icon: 'Users' },
  { key: 'players', title: 'Registered Players', icon: 'User' },
  { key: 'coaches', title: 'Coaches', icon: 'GraduationCap' },
  { key: 'regionalTeams', title: 'Regional Teams', icon: 'Map' },
  { key: 'officials', title: 'Active Officials', icon: 'Shield' },
  { key: 'regionAssociations', title: 'Region Associations', icon: 'Building2' },
  { key: 'leagues', title: 'Leagues', icon: 'Trophy' },
  { key: 'tournaments', title: 'Tournaments', icon: 'Medal' },
  { key: 'leagueMatches', title: 'League Matches', icon: 'Calendar' },
  { key: 'tournamentGames', title: 'Tournament Games', icon: 'Swords' },
];

const StatusLegend = ({ data = [] }) => (
  <div className="mt-3 space-y-1">
    {data.filter((d) => d.value > 0).map((item, index) => (
      <div key={item.name} className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground truncate pr-2">{item.name}</span>
        <span className="font-medium text-foreground">{item.value}</span>
      </div>
    ))}
  </div>
);

const AdminDashboard = () => {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isAdmin) {
        const data = await dashboardService.getAnalytics();
        setAnalytics(data);
      } else {
        setAnalytics(null);
      }
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const metricsData = useMemo(() => {
    if (!analytics?.summary) return [];
    return KPI_CONFIG.map(({ key, title, icon }) => {
      const metric = analytics.summary[key];
      return {
        title,
        icon,
        value: String(metric?.value ?? 0),
        change: metric?.change ?? '0',
        changeType: metric?.changeType ?? 'neutral',
        trend: metric?.trend ?? '',
      };
    });
  }, [analytics]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main
          className={`pt-16 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
          }`}
        >
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                <p className="text-muted-foreground">Loading dashboard analytics...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="max-w-7xl mx-auto p-6 space-y-8">
          <Breadcrumb />

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {isAdmin ? 'Admin Dashboard' : 'Dashboard'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {isAdmin
                  ? 'System-wide analytics across registrations, events, and match operations.'
                  : 'Your dashboard overview.'}
              </p>
              {lastUpdated && (
                <p className="text-xs text-muted-foreground mt-2">
                  Last updated {lastUpdated.toLocaleString()}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={loadDashboardData}
              className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors self-start"
            >
              <Icon name="RefreshCw" size={18} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {isAdmin && analytics && (
            <>
              {/* KPI Overview */}
              <DashboardSection
                title="Key Metrics"
                description="Headline counts with 30-day trend comparison for registrations"
                icon={<Icon name="LayoutDashboard" size={20} className="text-primary" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
                  {metricsData.map((metric) => (
                    <MetricsCard key={metric.title} {...metric} />
                  ))}
                </div>
              </DashboardSection>

              {/* Registration Trends */}
              <DashboardSection
                title="Registration Trend Analysis"
                description="Monthly new registrations over the last 6 months"
                icon={<Icon name="TrendingUp" size={20} className="text-primary" />}
              >
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <div className="xl:col-span-2 bg-card rounded-lg border border-border p-6 card-shadow">
                    <RegistrationTrendChart data={analytics.registrationTrend} />
                  </div>
                  <PendingApprovalsPanel pendingApprovals={analytics.pendingApprovals} />
                </div>
              </DashboardSection>

              {/* Registration Status Breakdown */}
              <DashboardSection
                title="Registration Status Breakdown"
                description="Current pipeline status across entity types"
                icon={<Icon name="PieChart" size={20} className="text-primary" />}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { title: 'Club Teams', data: analytics.registrationStatus?.teams },
                    { title: 'Players', data: analytics.registrationStatus?.players },
                    { title: 'Coaches', data: analytics.registrationStatus?.coaches },
                  ].map(({ title, data }) => (
                    <div key={title} className="bg-card rounded-lg border border-border p-6 card-shadow">
                      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
                      <StatusPieChart data={data} />
                      <StatusLegend data={data} />
                    </div>
                  ))}
                </div>
              </DashboardSection>

              {/* Events */}
              <DashboardSection
                title="Events — Leagues & Tournaments"
                description="Status distribution for league and tournament events"
                icon={<Icon name="Trophy" size={20} className="text-primary" />}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { title: 'Leagues by Status', data: analytics.eventStatus?.leagues },
                    { title: 'Tournaments by Status', data: analytics.eventStatus?.tournaments },
                  ].map(({ title, data }) => (
                    <div key={title} className="bg-card rounded-lg border border-border p-6 card-shadow">
                      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
                      <StatusPieChart data={data} />
                      <StatusLegend data={data} />
                    </div>
                  ))}
                </div>
              </DashboardSection>

              {/* Match Operations */}
              <DashboardSection
                title="Games & Match Operations"
                description="Match completion trends and operational status"
                icon={<Icon name="Activity" size={20} className="text-primary" />}
              >
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <div className="xl:col-span-2 bg-card rounded-lg border border-border p-6 card-shadow">
                    <h3 className="text-sm font-semibold text-foreground mb-4">
                      Completed Matches Trend (6 months)
                    </h3>
                    <MatchCompletionTrendChart data={analytics.matchCompletionTrend} />
                  </div>
                  <div className="bg-card rounded-lg border border-border p-6 card-shadow">
                    <h3 className="text-sm font-semibold text-foreground mb-2">Coach License Status</h3>
                    <StatusPieChart data={analytics.licenseStatus} />
                    <StatusLegend data={analytics.licenseStatus} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { title: 'League Match Status', data: analytics.matchStatus?.league },
                    { title: 'Tournament Game Status', data: analytics.matchStatus?.tournament },
                  ].map(({ title, data }) => (
                    <div key={title} className="bg-card rounded-lg border border-border p-6 card-shadow">
                      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
                      <StatusPieChart data={data} />
                      <StatusLegend data={data} />
                    </div>
                  ))}
                </div>
              </DashboardSection>

              {/* Regional Distribution */}
              <DashboardSection
                title="Regional Distribution"
                description="Top regions by club team and coach registrations"
                icon={<Icon name="MapPin" size={20} className="text-primary" />}
              >
                <div className="bg-card rounded-lg border border-border p-6 card-shadow">
                  <RegionalDistributionChart data={analytics.regionalDistribution} />
                </div>
              </DashboardSection>

              {/* Activity & Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <ActivityFeed activities={analytics.recentActivities} />
                </div>
                <QuickActions pendingActions={analytics.pendingActions} />
              </div>

              {/* Fixtures & Results */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <UpcomingFixtures fixtures={analytics.upcomingFixtures} />
                <RecentResults results={analytics.recentResults} />
              </div>
            </>
          )}

          {!isAdmin && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <UpcomingFixtures fixtures={[]} />
              <RecentResults results={[]} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
